import { isServiceOrder, isTransportOrder, type Order } from '#domain/Order.ts';
import type { Stop } from '#domain/Stop.ts';

import type { ApiOrder, ApiServiceOrder, ApiTransportOrder, ApiWriteOrder } from '../model/types.ts';
import { mapMaterials, mapMaterialsToWriteMaterials } from './materialAdapter.ts';
import { mapStop, mapStopToWriteStop } from './stopAdapter.ts';

const mapBaseOrder = (order: ApiOrder) => ({
    id: order.id,
    status: order.status,
    tourId: order.tour_id,
    tourName: order.tour_name,
    truck: order.truck_id ? { id: order.truck_id } : undefined,
    customerOrderId: undefined,
});

const mapOrder = (order: ApiOrder): Order => {
    if (order.type === 'service') {
        const serviceOrder = order as ApiServiceOrder;
        return {
            ...mapBaseOrder(serviceOrder),
            type: 'service',
            number: serviceOrder.execution_stop.name,
            customerName: '',
            executionStop: mapStop(serviceOrder.execution_stop),
            description: serviceOrder.order,
            category: serviceOrder.category,
            company: serviceOrder.company,
            note: serviceOrder.note,
        };
    }

    const transportOrder = order as ApiTransportOrder;
    return {
        ...mapBaseOrder(transportOrder),
        type: 'transport',
        number: transportOrder.number,
        customerName: transportOrder.customer_name,
        loadingStop: mapStop(transportOrder.loading_stop),
        unloadingStop: mapStop(transportOrder.unloading_stop),
        loadingComment: transportOrder.comments?.loading,
        unloadingComment: transportOrder.comments?.unloading,
        materials: mapMaterials(transportOrder.materials),
    };
};

export const mapOrderSummaries = (orders: ApiOrder[]): Order[] => orders.map(mapOrder);

export const mapOrderDetails = (order: ApiOrder): Order => {
    const summary = mapOrder(order);
    if (isTransportOrder(summary)) {
        const transportOrder = order as ApiTransportOrder;
        return { ...summary, materials: mapMaterials(transportOrder.materials) };
    }
    return summary;
};

const buildWriteComments = (loadingComment?: string, unloadingComment?: string) => {
    if (!loadingComment && !unloadingComment) {
        return undefined;
    }

    return {
        loading: loadingComment,
        unloading: unloadingComment,
    };
};

const ensureStop = (stop: Stop): Stop => ({
    ...stop,
    startTarget: stop.startTarget ?? '',
    endTarget: stop.endTarget ?? '',
});

export const mapOrderToWriteOrder = (order: Order): ApiWriteOrder => {
    if (isServiceOrder(order)) {
        return {
            type: 'service',
            order: order.description,
            category: order.category,
            company: order.company,
            note: order.note || undefined,
            execution_stop: mapStopToWriteStop(ensureStop(order.executionStop)),
        };
    }

    return {
        type: 'transport',
        number: order.number,
        customer_name: order.customerName,
        comments: buildWriteComments(order.loadingComment, order.unloadingComment),
        loading_stop: mapStopToWriteStop(ensureStop(order.loadingStop)),
        unloading_stop: mapStopToWriteStop(ensureStop(order.unloadingStop)),
        materials: mapMaterialsToWriteMaterials(order.materials ?? []),
    };
};
